import { useState } from 'react'
import { API_BASE_URL } from '@/lib/apiBaseUrl'
import { cn } from '@/lib/utils'

interface ManufacturerBrandLogoProps {
  brandName: string
  logoUrl?: string | null
  alt?: string
  imgClassName?: string
  fallbackClassName?: string
}

function resolveManufacturerBrandLogoUrl(value?: string | null): string | null {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (!trimmed) return null
  if (/^(?:https?:)?\/\//i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed
  }
  if (trimmed.startsWith('/api/')) {
    return trimmed
  }
  if (trimmed.startsWith('/')) {
    return `${API_BASE_URL}${trimmed}`
  }

  return trimmed
}

function buildManufacturerBrandFallbackLabel(brandName: string): string {
  const normalizedName = brandName.trim()
  if (!normalizedName) return 'MF'

  const initials = normalizedName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk.charAt(0))
    .join('')
    .toUpperCase()

  if (initials.length >= 2) {
    return initials
  }

  return normalizedName.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase() || 'MF'
}

export function ManufacturerBrandLogo({
  brandName,
  logoUrl,
  alt,
  imgClassName,
  fallbackClassName,
}: ManufacturerBrandLogoProps) {
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null)
  const normalizedLogoUrl = resolveManufacturerBrandLogoUrl(logoUrl)
  const shouldDisplayLogo = Boolean(normalizedLogoUrl && failedLogoUrl !== normalizedLogoUrl)

  if (shouldDisplayLogo && normalizedLogoUrl) {
    return (
      <img
        src={normalizedLogoUrl}
        alt={alt ?? brandName}
        width={160}
        height={160}
        className={cn('h-full w-full object-contain', imgClassName)}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        draggable={false}
        onError={() => setFailedLogoUrl(normalizedLogoUrl)}
      />
    )
  }

  return (
    <span className={cn('text-xs font-semibold uppercase text-muted-foreground', fallbackClassName)}>
      {buildManufacturerBrandFallbackLabel(brandName)}
    </span>
  )
}
